import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ShoppingCart, ShieldCheck, Users, TrendingUp, Package } from "lucide-react";

const StatCard = ({ label, value, icon: Icon, testId }) => (
    <div className="border border-zinc-200 bg-white p-6" data-testid={testId}>
        <div className="flex items-start justify-between mb-6">
            <div className="tag-label">{label}</div>
            <Icon className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
        </div>
        <div className="font-display text-4xl font-black tracking-tight">{value}</div>
    </div>
);

const OverviewPage = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState({ orders: 0, warranties: 0, users: 0 });
    const [recent, setRecent] = useState({ orders: [], warranties: [] });

    useEffect(() => {
        (async () => {
            try {
                const [oRes, wRes] = await Promise.all([
                    api.get("/orders"),
                    api.get("/warranties"),
                ]);
                let usersCount = 0;
                if (user?.role === "admin") {
                    try {
                        const uRes = await api.get("/users");
                        usersCount = uRes.data.length;
                    } catch (_) {}
                }
                setStats({ orders: oRes.data.length, warranties: wRes.data.length, users: usersCount });
                setRecent({ orders: oRes.data.slice(0, 5), warranties: wRes.data.slice(0, 5) });
            } catch (_) {}
        })();
    }, [user]);

    return (
        <div className="p-6 md:p-10 lg:p-12 max-w-[1400px] animate-fade-up" data-testid="overview-page">
            <div className="mb-12">
                <div className="tag-label mb-3">Genel Bakış</div>
                <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[0.95] mb-2">
                    Hoş geldiniz, {user?.name}.
                </h1>
                <p className="text-zinc-600 text-base max-w-xl">
                    {user?.role === "admin"
                        ? "Tüm kullanıcıların sipariş ve garanti verilerine genel bir bakış."
                        : user?.role === "uretici"
                            ? "Tüm bayilerin sipariş ve garanti verilerini görüntüleyebilirsiniz."
                            : "Kendi sipariş ve garanti kayıtlarınızı buradan takip edin."}
                </p>
            </div>

            <div className={`grid gap-px bg-zinc-200 border border-zinc-200 mb-12 ${user?.role === "admin" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-3"}`}>
                <StatCard label="Toplam Sipariş" value={stats.orders} icon={ShoppingCart} testId="stat-orders" />
                <StatCard label="Toplam Garanti" value={stats.warranties} icon={ShieldCheck} testId="stat-warranties" />
                {user?.role === "admin" && (
                    <StatCard label="Kayıtlı Kullanıcı" value={stats.users} icon={Users} testId="stat-users" />
                )}
                {user?.role === "admin" ? (
                    <StatCard label="Aktif Modül" value="4" icon={TrendingUp} testId="stat-modules" />
                ) : (
                    <StatCard
                        label="Stoktaki Sipariş"
                        value={Math.max(stats.orders - stats.warranties, 0)}
                        icon={Package}
                        testId="stat-in-stock"
                    />
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="border border-zinc-200 bg-white">
                    <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                        <div className="tag-label">Son Siparişler</div>
                        <ShoppingCart className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                    </div>
                    {recent.orders.length === 0 ? (
                        <div className="p-12 text-center text-sm text-zinc-500">Henüz sipariş yok</div>
                    ) : (
                        <ul className="divide-y divide-zinc-200">
                            {recent.orders.map((o) => (
                                <li key={o.id} className="px-6 py-4 flex items-center justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="font-mono text-xs text-zinc-500">{o.order_no}</div>
                                        <div className="font-semibold text-sm truncate">{o.klima_modeli} · İlave Akü: {o.ilave_aku}</div>
                                    </div>
                                    <div className="text-xs text-zinc-500 whitespace-nowrap">
                                        {new Date(o.created_at).toLocaleDateString("tr-TR")}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="border border-zinc-200 bg-white">
                    <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                        <div className="tag-label">Son Garantiler</div>
                        <ShieldCheck className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                    </div>
                    {recent.warranties.length === 0 ? (
                        <div className="p-12 text-center text-sm text-zinc-500">Henüz garanti yok</div>
                    ) : (
                        <ul className="divide-y divide-zinc-200">
                            {recent.warranties.map((w) => (
                                <li key={w.id} className="px-6 py-4 flex items-center justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="font-mono text-xs text-zinc-500">{w.warranty_no}</div>
                                        <div className="font-semibold text-sm truncate">{w.klima_modeli} · İlave Akü: {w.ilave_aku}</div>
                                    </div>
                                    <div className="text-xs text-zinc-500 whitespace-nowrap">
                                        {new Date(w.created_at).toLocaleDateString("tr-TR")}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OverviewPage;
